import { FC, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast, ToastContainer } from 'react-toastify'
import CN from 'classnames'
import {
  NotificationProcessCatalogueEntry,
  useDeleteEmailAlertGroup,
  useGetEmailAlertCardsList,
  useGetNotificationProcessCatalogue,
} from 'framework/api/methods'

import { PopOut, PopUpAlert } from 'components/atoms'
import { Button } from 'components/atoms/Button'
import {
  AddEmailAlertGroup,
  UpdateEmailAlertGroup,
} from 'components/DrawerModals/EmailAlertGroup'
import { useDebouncedValue } from 'utils'

import {
  EmailAlertsCardList,
  NotificationRecipientCard,
} from './EmailAlertsCardList'
import {
  ALL_OUTCOMES,
  ALL_PARTNERS_OPTION,
  EmailAlertsFilterBar,
  PartnerFilterOption,
  usePartnerOptions,
} from './EmailAlertsFilterBar'
import { EmailAlertsRecipientsModal } from './EmailAlertsRecipientsModal'
import {
  EmailAlertsSummaryBar,
  OtherPartnerMatch,
} from './EmailAlertsSummaryBar'
import {
  buildCardListRows,
  cardKey,
  CataloguePartner,
  countNotConfigured,
  countRecipients,
  EmailAlertGroupPrefill,
  OutcomeFilter,
} from './processCatalogue'

import 'react-toastify/dist/ReactToastify.css'

/**
 * BDL20-4262: cards are fetched unpaged for the partner filter and paged client-side, so a
 * process's Success/Failure groups are always paired on one page and "Not configured"
 * placeholders can sit in the same list. Groups are one per (partner, ServiceName) — well under this.
 */
const ALL_CARDS_TAKE = 1000

export interface EmailNotificationRecipientsProps {
  [x: string]: any
}

export const EmailNotificationRecipients: FC<
  EmailNotificationRecipientsProps
> = ({ className }: EmailNotificationRecipientsProps) => {
  const { userDetails }: any = useSelector<any>((state) => state.user)
  const canEdit =
    userDetails?.userPermissions?.includes('EmailAlertsEditAccess') ?? false

  const [selectedPartner, setSelectedPartner] =
    useState<PartnerFilterOption>(ALL_PARTNERS_OPTION)
  const [selectedProcess, setSelectedProcess] = useState('')
  const [selectedOutcome, setSelectedOutcome] =
    useState<OutcomeFilter>(ALL_OUTCOMES)
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 400)
  const [pageNumber, setPageNumber] = useState(1)
  const [take, setTake] = useState(10)
  const [activeCard, setActiveCard] =
    useState<NotificationRecipientCard | null>(null)
  const [editingCard, setEditingCard] =
    useState<NotificationRecipientCard | null>(null)
  const [deletingCard, setDeletingCard] =
    useState<NotificationRecipientCard | null>(null)
  const [addGroupDrawerIsOpen, setAddGroupDrawerIsOpen] = useState(false)
  const [addGroupPrefill, setAddGroupPrefill] =
    useState<EmailAlertGroupPrefill | null>(null)

  const { mutate: deleteGroup, isLoading: isDeletingGroup } =
    useDeleteEmailAlertGroup()

  const { options: partnerOptions } = usePartnerOptions()
  const { data: catalogueData } = useGetNotificationProcessCatalogue()
  // Empty when the catalogue request failed: cards still render, just unpaired and without placeholders.
  const catalogueEntries: NotificationProcessCatalogueEntry[] =
    catalogueData?.processes ?? []

  const { data, isLoading, refetch } = useGetEmailAlertCardsList({
    partnerId: selectedPartner.id || undefined,
    take: ALL_CARDS_TAKE,
    skip: 0,
  })
  const allCards: NotificationRecipientCard[] = data?.cards ?? []

  // The server search is the only thing that can match recipient emails (cards do not carry
  // them); its hits are merged with the client-side text match over the card fields.
  const searchEnabled = !!debouncedSearch
  const { data: searchData, refetch: refetchSearch } =
    useGetEmailAlertCardsList(
      {
        partnerId: selectedPartner.id || undefined,
        search: debouncedSearch || undefined,
        take: ALL_CARDS_TAKE,
        skip: 0,
      },
      { enabled: searchEnabled },
    )
  const searchMatchedKeys = useMemo<Set<string> | null>(() => {
    if (!searchEnabled) return null
    const keys = new Set<string>()
    ;(searchData?.cards ?? []).forEach((card: NotificationRecipientCard) =>
      keys.add(cardKey(card.partnerId, card.serviceName)),
    )
    return keys
  }, [searchData, searchEnabled])

  const cataloguePartners = useMemo<CataloguePartner[]>(
    () =>
      partnerOptions.map((option) => ({
        id: option.id,
        name: option.label,
        code: option.code,
      })),
    [partnerOptions],
  )
  const selectedCataloguePartner = useMemo<CataloguePartner | null>(
    () =>
      selectedPartner.id
        ? {
            id: selectedPartner.id,
            name: selectedPartner.label,
            code: selectedPartner.code,
          }
        : null,
    [selectedPartner.id, selectedPartner.label, selectedPartner.code],
  )

  const rows = useMemo(
    () =>
      buildCardListRows({
        cards: allCards,
        entries: catalogueEntries,
        partners: cataloguePartners,
        selectedPartner: selectedCataloguePartner,
        outcomeFilter: selectedOutcome,
        processFilter: selectedProcess,
        searchTerm: debouncedSearch,
        searchMatchedKeys,
      }),
    [
      allCards,
      catalogueEntries,
      cataloguePartners,
      selectedCataloguePartner,
      selectedOutcome,
      selectedProcess,
      debouncedSearch,
      searchMatchedKeys,
    ],
  )

  const totalCount = rows.length
  const skip = pageNumber === 1 ? 0 : take * (pageNumber - 1)
  const pagedRows = rows.slice(skip, skip + take)
  const totalRecipientCount = countRecipients(rows)
  const notConfiguredCount = countNotConfigured(rows)

  const refetchAll = () => {
    refetch()
    if (searchEnabled) refetchSearch()
  }

  // Only fired when a specific partner is selected and a search is active — surfaces other
  // partners whose recipients/reports also match, so the user can jump straight to them
  // (mirrors the design mock's "Other partners matching" affordance).
  const crossPartnerSearchEnabled = !!selectedPartner.id && !!debouncedSearch
  const { data: crossPartnerData } = useGetEmailAlertCardsList(
    {
      search: debouncedSearch || undefined,
      take: 50,
      skip: 0,
    },
    { enabled: crossPartnerSearchEnabled },
  )

  const otherPartnersMatching: OtherPartnerMatch[] = useMemo(() => {
    if (!crossPartnerSearchEnabled) return []
    const seen = new Map<string, OtherPartnerMatch>()
    ;(crossPartnerData?.cards ?? []).forEach(
      (card: NotificationRecipientCard) => {
        if (card.partnerId === selectedPartner.id) return
        if (!seen.has(card.partnerId)) {
          seen.set(card.partnerId, {
            id: card.partnerId,
            name: card.partnerName ?? '',
            code: card.partnerCode ?? '',
          })
        }
      },
    )
    return Array.from(seen.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 4)
  }, [crossPartnerData, crossPartnerSearchEnabled, selectedPartner.id])

  const handleJumpToPartner = (partner: OtherPartnerMatch) => {
    setSelectedPartner({
      id: partner.id,
      value: partner.id,
      label: partner.name,
      code: partner.code,
    })
  }

  useEffect(() => {
    setPageNumber(1)
  }, [debouncedSearch, selectedProcess, selectedOutcome, selectedPartner.id])

  const notify = (message: string, type: 'success' | 'error') => {
    const options = {
      position: 'top-right' as const,
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    }
    if (type === 'error') toast.error(message, options)
    if (type === 'success') toast.success(message, options)
  }

  const openAddGroupDrawer = (prefill: EmailAlertGroupPrefill | null) => {
    setAddGroupPrefill(prefill)
    setAddGroupDrawerIsOpen(true)
  }

  const closeAddGroupDrawer = () => {
    setAddGroupDrawerIsOpen(false)
    setAddGroupPrefill(null)
  }

  const handleConfirmDeleteGroup = () => {
    if (!deletingCard) return

    deleteGroup(
      {
        partnerId: deletingCard.partnerId,
        serviceName: deletingCard.serviceName,
      },
      {
        onSuccess: () => {
          notify(`Deleted ${deletingCard.serviceName}`, 'success')
          setDeletingCard(null)
          refetchAll()
        },
        onError: ({ response: { data: errData } }: any) => {
          notify(errData?.message || 'Could not delete email group', 'error')
        },
      },
    )
  }

  return (
    <div
      className={CN(
        'flex items-center justify-center bg-N-50 h-full w-full px-8 py-5',
        {},
        className,
      )}>
      <div className='flex flex-col h-full w-full rounded-lg bg-white shadow-md px-5 py-7 overflow-y-auto styled-scroll'>
        <div className='flex items-start justify-between gap-4 flex-wrap'>
          <div>
            <h2 className='text-lg font-bold text-N-800'>
              Email Alert Recipients
            </h2>
            <p className='text-[13px] text-N-500 mt-1'>
              Each report keeps its own recipient list per partner — changes
              here never affect other reports.
            </p>
          </div>
          {canEdit && (
            <Button
              appearance='outline'
              size='sm'
              onClick={() => openAddGroupDrawer(null)}>
              <i className='ri-add-line pr-[7px]'></i>
              New Email Group
            </Button>
          )}
        </div>

        {!canEdit && (
          <PopOut appearance='warning' className='mt-4'>
            <span className='font-bold'>Read-only access.</span> You can view
            recipients but need an authorized role to make changes.
          </PopOut>
        )}

        <EmailAlertsFilterBar
          selectedPartner={selectedPartner}
          onPartnerChange={setSelectedPartner}
          selectedProcess={selectedProcess}
          onProcessChange={setSelectedProcess}
          selectedOutcome={selectedOutcome}
          onOutcomeChange={setSelectedOutcome}
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          onSearchClear={() => setSearchInput('')}
        />

        <EmailAlertsSummaryBar
          selectedPartnerLabel={selectedPartner.label}
          visibleCardCount={pagedRows.length}
          totalCardCount={totalCount}
          totalRecipientCount={totalRecipientCount}
          notConfiguredCount={notConfiguredCount}
          searchTerm={debouncedSearch}
          otherPartnersMatching={otherPartnersMatching}
          onJumpToPartner={handleJumpToPartner}
        />

        <EmailAlertsCardList
          rows={pagedRows}
          isLoading={isLoading}
          partnerFilterIsAll={!selectedPartner.id}
          canEdit={canEdit}
          onManageClick={setActiveCard}
          onEditClick={setEditingCard}
          onDeleteClick={setDeletingCard}
          onSetUpClick={openAddGroupDrawer}
          pageNumber={pageNumber}
          take={take}
          totalCount={totalCount}
          onPageChange={setPageNumber}
          onTakeChange={(value) => {
            setTake(value)
            setPageNumber(1)
          }}
        />

        {activeCard && (
          <EmailAlertsRecipientsModal
            card={activeCard}
            canEdit={canEdit}
            onClose={() => setActiveCard(null)}
            notify={notify}
            onRecipientCountChanged={refetchAll}
          />
        )}

        {canEdit && addGroupDrawerIsOpen && (
          <AddEmailAlertGroup
            isActive={addGroupDrawerIsOpen}
            onCloseClick={closeAddGroupDrawer}
            notify={notify}
            onCreated={refetchAll}
            prefill={addGroupPrefill}
            onManageExisting={setActiveCard}
          />
        )}

        {canEdit && editingCard && (
          <UpdateEmailAlertGroup
            isActive={!!editingCard}
            card={editingCard}
            onCloseClick={() => setEditingCard(null)}
            notify={notify}
            onUpdated={refetchAll}
          />
        )}

        {canEdit && deletingCard && (
          <PopUpAlert
            isActive={!!deletingCard}
            customIcon='delete'
            title='Delete Email Group?'
            message={`This will permanently remove "${deletingCard.serviceName}" and all ${deletingCard.recipientCount} recipient(s) for ${deletingCard.partnerName}. This is not reversible.`}
            cancelButtonText='Cancel'
            confirmButtonText={isDeletingGroup ? 'Deleting…' : 'Yes, delete it'}
            onCancelClick={() => setDeletingCard(null)}
            onConfirmClick={handleConfirmDeleteGroup}
            onOverlayClick={() => setDeletingCard(null)}
          />
        )}

        <ToastContainer
          position='top-right'
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme='colored'
          className='toast-container'
        />
      </div>
    </div>
  )
}

EmailNotificationRecipients.defaultProps = {}

export default EmailNotificationRecipients
